import React from 'react';

import type { DndDragHandleProps } from '~/components/core/DndOrderedList/DndOrderedList.types';
import Text from '~/components/core/TextComponents/Text';
import ThreeDotsMenu from '~/components/core/ThreeDotsMenu';
import ReorderDragHandle from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/components/ReorderDragHandle';
import Switch from '~/components/ui/atoms/Switch';
import { serverDateToLocal } from '~/DateTimeUtils';
import cn from '~/Utils/cn';
import { getLobDescription } from '~/Utils/lobUtils';

import { gridClassFor } from './GlobalFieldsTableHeader';
import type { GlobalFieldRow as GlobalFieldRowType } from './types';
import { PLACEMENT_LABELS } from './types';

interface GlobalFieldRowProps {
  field: GlobalFieldRowType;
  dragHandleProps: DndDragHandleProps;
  isDragDisabled: boolean;
  isProcessing: boolean;
  showPlacementColumn: boolean;
  subOrgIdToName: Record<number, string>;
  lobConfigurationsDict: Record<string, unknown>;
  subOrganizationsEnabled: boolean;
  onEdit: (field: GlobalFieldRowType) => void;
  onDelete: (field: GlobalFieldRowType) => void | Promise<void>;
  onEnabledChanged: (field: GlobalFieldRowType) => void | Promise<void>;
}

const GlobalFieldRow: React.FC<GlobalFieldRowProps> = ({
  field,
  dragHandleProps,
  isDragDisabled,
  isProcessing,
  showPlacementColumn,
  subOrgIdToName,
  lobConfigurationsDict,
  subOrganizationsEnabled,
  onEdit,
  onDelete,
  onEnabledChanged,
}) => {
  // `string`, not `ReactNode`: every cell here renders text, and `Text` rejects a nullable child.
  const cell = (content: string, key: string) => (
    <Text key={key} variant={Text.VARIANTS.SM} className="truncate">
      {content}
    </Text>
  );

  const lobLabel = field.is_all_lobs
    ? 'All LOBs'
    : // A LOB missing from the org config has no description; fall back to its key so the cell
      // names the LOB instead of rendering blank.
      field.lobs.map((lobKey) => getLobDescription(lobKey, lobConfigurationsDict) ?? lobKey).join(', ');
  const subOrgLabel = field.is_all_sub_orgs
    ? 'All sub-organizations'
    : field.sub_organization_ids.map((id) => subOrgIdToName[id] ?? `#${id}`).join(', ');
  // Only meaningful when the organization has sub-organizations at all; otherwise the scope is
  // the LOB list alone and "All sub-organizations" would be noise.
  const contextLabel = subOrganizationsEnabled ? `${lobLabel} · ${subOrgLabel}` : lobLabel;

  const actionOptions = [
    { onClick: () => onEdit(field), node: 'Edit', key: 'editField' },
    {
      onClick: () => onDelete(field),
      withConfirmProps: {
        title: 'Delete Global Field?',
        contentText: `"${field.definition?.desc ?? field.key}" will be removed from every configuration it was projected into. Values already captured on existing claims are not deleted.`,
        primaryButtonName: 'Delete',
      },
      node: 'Delete',
      key: 'deleteField',
    },
  ];

  return (
    <div
      className={cn(
        gridClassFor(showPlacementColumn),
        'border-0 border-b border-solid border-slate-300 bg-white py-8',
        // A disabled field keeps its place in the order but reads as inactive. Dimming the
        // children rather than the row keeps the row's own background opaque while dragging.
        field.is_enabled ? '' : '*:opacity-60'
      )}
    >
      <ReorderDragHandle dragHandleProps={dragHandleProps} disabled={isDragDisabled} />
      {cell(String(field.definition?.desc ?? ''), 'desc')}
      {cell(field.key, 'key')}
      {cell(String(field.definition?.type ?? ''), 'type')}
      {cell(contextLabel, 'context')}
      {showPlacementColumn ? cell(PLACEMENT_LABELS[field.placement], 'placement') : null}
      {cell(serverDateToLocal(field.last_updated_on), 'lastUpdated')}
      {cell(field.updated_by, 'updatedBy')}
      {/* Named after the field it belongs to: in a reorderable list a positional selector follows
          whichever row happens to sit at that index, which changes as soon as the order does. */}
      <Switch
        aria-label={`Enable — ${field.definition?.desc ?? field.key}`}
        onChange={() => onEnabledChanged(field)}
        disabled={isProcessing}
        checked={!!field.is_enabled}
      />
      <div>
        {/* Disabled alongside the switch: Delete must not stay clickable while a delete, enable
            or reorder write is still in flight. */}
        <ThreeDotsMenu options={actionOptions} disabled={isProcessing} />
      </div>
    </div>
  );
};

export default GlobalFieldRow;
